import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { addPrescriptionProductLines } from '../../lib/prescriptionAutoProduct';
import { mergeNewPrescriptions, isDpmPrescription } from '../../lib/dpmPrescriptions';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import DyrejournalSalePrescription from '../../components/dyrejournal/salePrescription';
import { printViaIframe } from '../../lib/printHelper';
import PrescriptionPackageTotal from './prescriptionPackageTotal';

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appAlert } from '../../lib/dialogs';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

// Toggle fields store 0/1; older data may hold booleans.
const isFlagOn = value => value === true || value === 1 || value === '1';

@observer
class PrescriptionListMini extends Component {
	constructor(props) {
        super(props);
        this.state = {
            printData: {},
        };
    }

    componentDidMount() {
        this.loadPrintData();
    }

    componentDidUpdate(prevProps) {
        // Callers often pass a fresh array literal on every render (e.g. the e-mail
        // prescription drawer re-renders per keystroke). Compare content, not identity,
        // so print data is not reloaded (animal + owner GETs) for every keystroke.
        if (this.prescriptionsSignature(prevProps.prescriptions) !== this.prescriptionsSignature(this.props.prescriptions)) {
            this.loadPrintData();
        }
    }

    prescriptionsSignature = (prescriptions = []) => (prescriptions || [])
        .map(p => `${p?.id || p?.md5 || p?.uuidv4 || ''}:${p?.animal || ''}`)
        .join('|');

    loadPrintDataForPrescription = async prescription => {
        const { animalStore, visitorStore, journalStore } = this.props.stores;
        const { newJournal } = journalStore;
        const animalId = prescription.animal || newJournal?.animal;
        if (!animalId) return null;
        try {
            // load() returns undefined when the API responds with an error.
            const currentAnimals = await animalStore.load({ query: { id: animalId }, skipUpdate: true });
            const currentAnimal = currentAnimals?.[0];
            if (!currentAnimal) return null;
            const currentVisitors = await visitorStore.load({ query: { id: currentAnimal.owner }, skipUpdate: true });
            return {
                currentAnimal,
                currentVisitor: currentVisitors?.[0],
            };
        } catch (err) {
            console.error('loadPrintDataForPrescription failed', err);
            return null;
        }
    }

    loadPrintData = async () => {
        const { prescriptions = [] } = this.props;
        if (!prescriptions.length) return;

        const printData = {};
        for (let i = 0; i < prescriptions.length; i++) {
            const prescription = prescriptions[i];
            const data = await this.loadPrintDataForPrescription(prescription);
            if (!data) continue;
            const key = prescription.id || prescription.md5 || prescription.uuidv4;
            printData[key] = data;
        }
        this.setState({ printData });
    }

    saveJournal = async () => {
        const { saveJournal = () => {} } = this.props;
        saveJournal();
    }

    onClickCreatePrescription = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { drawerLevel = 1, saleIsLocked = false } = this.props;

        // console.log('onClickCreatePrescription', e, drawerLevel);
        const { appState, saleStore, animalStore, userStore, productStore } = this.props.stores;
        const { drawerHeightLarge } = appState;

        const { journalStore } = this.props.stores;

        // Default the journal's responsible veterinarian to the logged-in user when none is set,
        // so creating a prescription never forces a manual vet pick. Keeps the journal's vet if one
        // is already chosen. Mirrors the auto-fill in listAnimalMini's "New prescription" flow.
        const { user } = userStore;
        if (!journalStore.newJournal.veterinary && user?.id) {
            journalStore.updateObjectKeyValue('newJournal', 'veterinary', user.id);
        }

        await this.saveJournal();

        appState.openDrawer('addPrescription', {
            height: drawerHeightLarge,
            isNew: true,
            hideAdmin: true,
            saleIsLocked,
            callback: async prescription => {
                appState.toggleDrawer(false, drawerLevel + 1);
                if (prescription) {
                    // console.log('onClickCreatePrescription callback', prescription);
                    const { newJournal } = journalStore;
                    const { prescriptions } = newJournal;
                    const isFound = prescriptions.find(a => a.id === prescription.id);
                    if (!isFound) {
                        journalStore.updateObjectKeyValue('newJournal', 'prescriptions', [...prescriptions, prescription]);
                        // Feature: auto-add the clinic's prescription fee product once per sale.
                        // Never blocks the prescription; the helper swallows its own errors.
                        await addPrescriptionProductLines({
                            saleStore,
                            productStore,
                            userStore,
                            animalId: prescription.animal || newJournal.animal,
                        });
                    }
                }
                await this.saveJournal();
            },
        }, drawerLevel + 1);
    }

    onClickDpmLaunch = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { drawerLevel = 1 } = this.props;
        const { appState, journalStore, userStore } = this.props.stores;
        const { drawerHeightLarge } = appState;

        // Same vet default as «Ny resept»: a prescription needs a responsible vet.
        const { user } = userStore;
        if (!journalStore.newJournal.veterinary && user?.id) {
            journalStore.updateObjectKeyValue('newJournal', 'veterinary', user.id);
        }
        await this.saveJournal();

        const journalId = journalStore.newJournal?.id;
        if (!journalId) {
            return;
        }
        const response = await util.fetchApi('/api/integrations/dpm/launch', { method: 'GET' }, { journal: journalId });
        if (!response || response.status >= 400 || !response.data?.url) {
            appState.addToast(response?.message || 'Kunne ikke åpne DyreID e-resept.');
            return;
        }
        this.dpmClosed = false;
        appState.openDrawer('dpmLauncher', {
            height: drawerHeightLarge,
            url: response.data.url,
            callback: () => this.onDpmClosed(journalId),
        }, drawerLevel + 1);
    }

    // Runs once when the DPM drawer unmounts: pull the journal fresh (the backend
    // pushed any delivered prescription onto it), merge, add the fee line per new
    // prescription, then save. Guarded so a second call is a no-op.
    onDpmClosed = async journalId => {
        if (this.dpmClosed) {
            return;
        }
        this.dpmClosed = true;
        const { journalStore, saleStore, productStore, userStore } = this.props.stores;
        const loaded = await journalStore.load(journalId, false, { skipUpdate: true });
        const fresh = Array.isArray(loaded) ? loaded[0] : null;
        if (!fresh || !fresh.id) {
            return;
        }
        const { prescriptions = [] } = journalStore.newJournal;
        const { merged, added } = mergeNewPrescriptions(prescriptions, fresh.prescriptions);
        if (added.length === 0) {
            return;
        }
        journalStore.updateObjectKeyValue('newJournal', 'prescriptions', merged);
        const addedList = added;
        for (const prescription of addedList) {
            // eslint-disable-next-line no-await-in-loop
            await addPrescriptionProductLines({
                saleStore,
                productStore,
                userStore,
                animalId: prescription.animal || journalStore.newJournal.animal,
            });
        }
        await this.saveJournal();
    }

    onClickRemovePrescription = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { journal, id, md5 } = e.target.closest('.prescription').dataset;

        // console.log('onClickRemovePrescription', { journal, id, md5 });
        const { journalStore } = this.props.stores;
        const { newJournal } = journalStore;
        journalStore.removeArrayObject({ field: 'prescriptions', id: journal, md5 });
        const { prescriptions } = newJournal;
        const newPrescriptions = prescriptions.filter(p => p.md5 !== md5);
        journalStore.updateObjectKeyValue('newJournal', 'prescriptions', newPrescriptions);
        await this.saveJournal();
    }

    onClickPrescription = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.prescription').dataset;
        // console.log('onClickPrescription', id);
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;
        const { journalStore } = this.props.stores;

        appState.openDrawer('viewPrescription', {
            height: drawerHeightMediumLarge,
            id: parseInt(id, 10),
            hideAdmin: true,
        }, drawerLevel + 1);
    }

    onClickPrintPrescription = async (e, prescription) => {
        e.stopPropagation();

        const key = prescription.id || prescription.md5 || prescription.uuidv4;
        const { printData = {} } = this.state;
        let data = printData[key];
        if (!data) {
            // The prefetch in loadPrintData may still be running or may have
            // failed. Load what this one prescription needs right now.
            data = await this.loadPrintDataForPrescription(prescription);
            if (data) {
                this.setState(prevState => ({ printData: { ...prevState.printData, [key]: data } }));
            }
        }
        if (!data) {
            (await appAlert('Laster data, vennligst prøv igjen.'));
            return;
        }

        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        const prescriptionComponent = <IntlProvider definition={languageDef}>
            <DyrejournalSalePrescription
                stores={this.props.stores}
                prescription={prescription}
                currentAnimal={data.currentAnimal}
                currentVisitor={data.currentVisitor}
            />;
        </IntlProvider>;
        const tempDiv = document.createElement('div');
        tempDiv.className = 'printPrescription';
        document.body.appendChild(tempDiv);
        render(prescriptionComponent, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);

        printViaIframe(htmlContent);
    }

    onClickEmailPrescription = async (e, prescription) => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.prescription').dataset;
        // console.log('onClickPrescription', id);
        const { drawerLevel = 1, journal } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightMediumLarge } = appState;

        appState.openDrawer('emailPrescription', {
            height: drawerHeightMediumLarge,
            prescription,
            journal,
        }, drawerLevel + 1);
    }

    onClickCopyPrescription = async (e, prescription) => {
        e.stopPropagation();
        e.preventDefault();

        const { drawerLevel = 1 } = this.props;
        const { appState, journalStore, prescriptionStore, saleStore, animalStore, userStore } = this.props.stores;
        const { newSale } = saleStore;
        const { user } = userStore;

        // 1. Load original journal if prescription has journal reference
        let originalJournal = null;
        if (prescription.journal) {
            const journals = await journalStore.load({
                query: { id: prescription.journal },
                skipUpdate: true,
            });
            originalJournal = journals?.[0];
        }

        // 2. Get current animal from the prescription
        const animalId = prescription.animal;
        const now = new Date();

        // 3. Get visitor and customer from sale
        let visitorId = null;
        let customerId = prescription.customer;
        if (newSale.visitors && newSale.visitors.length > 0) {
            visitorId = newSale.visitors[0].id;
        } else {
            // Load animal to get owner
            const animals = await animalStore.load({ query: { id: animalId }, skipUpdate: true });
            const animal = animals?.[0];
            visitorId = animal?.owner;
        }

        // 4. Prepare new journal data (blank journal, only copy chiefComplaint)
        const newJournalData = {
            date: now,
            animal: animalId,
            visitor: visitorId,
            chiefComplaint: originalJournal?.chiefComplaint || '',
        };

        // 5. Insert the new journal via API first
        const journalResponse = await journalStore.insert(newJournalData);
        const createdJournal = journalResponse?.data?.journal;

        if (createdJournal && createdJournal.id) {
            // 6. Create prescription document in prescription collection
            const newPrescriptionData = {
                date: now,
                animal: animalId,
                customer: customerId,
                veterinary: user.id,
                journal: createdJournal.id,
                // Copy drugs array with all details
                drugs: (prescription.drugs || []).map(drug => ({
                    ...drug,
                })),
                description: prescription.description || '',
            };

            const prescriptionResponse = await prescriptionStore.insert(newPrescriptionData);
            const createdPrescription = prescriptionResponse?.data?.prescription;

            // 7. Update journal with prescription in database
            if (createdPrescription && createdPrescription.id) {
                createdJournal.prescriptions = [createdPrescription];
                // Save journal with prescription reference
                await journalStore.save({
                    id: createdJournal.id,
                    prescriptions: [createdPrescription],
                });
            }

            // 8. Add the created journal to newSale.journals
            const { journals = [] } = newSale;
            saleStore.updateObjectKeyValue('newSale', 'journals', [...journals, createdJournal]);
        }

        // 9. Close drawer - user stays on sale page with new journal
        appState.toggleDrawer(false, drawerLevel);
    }

    render() {
        const {
            prescriptions = [],
            saleIsLocked = false,
            journal,
            showButtons = true,
            classNames = 'mx-4',
        } = this.props;
        const { userStore } = this.props.stores;
        const { user, isAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { customer = {} } = userStore;
        const { featureFlags = {} } = customer;
        const showDpm = isFlagOn(featureFlags.dpm);

        return (<>
           {(prescriptions && prescriptions.length > 0) && <>
                {prescriptions.filter(p => p && (p.id || p.uuidv4)).map(prescription => <>
                    <div
                        class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} ${classNames} d-flex flex-column box-container mt-2 p-3 prescription font-style-narrow`}
                        onClick={this.onClickPrescription}
                        data-journal={journal}
                        data-id={prescription.id}
                        data-md5={prescription.md5}
                    >
                        <div class={`d-flex flex-row justify-content-between px-3 py-1`}>
                            {prescription.date && <div class='flex-fill'>
                                {util.formatDate(prescription.date, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}
                            </div>}
                            {prescription.endDate && <div class='ms-2'>
                                {util.formatDate(prescription.endDate, { hour12: false, hour: '2-digit', minute: '2-digit',  locale: 'nb-NO' })}
                            </div>}
                            {isDpmPrescription(prescription) && <div class='ms-2'>
                                <span class='badge bg-info text-dark rounded-pill'>
                                    <i class='fa-duotone fa-file-prescription me-1' />
                                    <Text id='dyrejournal.prescription.dpmBadge'>DyreID e-prescription</Text>
                                    {prescription.drfStatus ? ` · ${prescription.drfStatus}` : ''}
                                    {prescription.drfReferenceNumber ? ` · ${prescription.drfReferenceNumber}` : ''}
                                </span>
                            </div>}
                            {!saleIsLocked && <div class='ms-2' onClick={this.onClickRemovePrescription} data-id={prescription.id} style='font-size: 1.4em;'>
                                <i class='fa-solid fa-circle-xmark text-danger' />
                            </div>}
                            {/* <button
                                class={`btn btn-link ms-2 rounded-pill`}
                                onClick={(e) => this.onClickDeletePrescription(e, prescription)}
                            >
                                <i class='fa-solid fa-trash' /> <Text id='dyrejournal.delete-prescription'>Delete prescription</Text>
                            </button> */}
                        </div>
                        {prescription.drugs && prescription.drugs.length > 0 && <>
                            {prescription.drugs.map(drug => <>
                                <div class='d-flex flex-column border box-container mx-0 mt-2 bg-light p-3'>
                                    <div class={`d-flex flex-row justify-content-between px-3 py-1`}>
                                        <div class='text-muted'>
                                            <Text id='dyrejournal.prescription.drug'>Rp.</Text>
                                        </div>
                                        <div class='flex-fill ms-2'>
                                            {drug.qty || 1} x {drug.rpText || drug.navnFormStyrke} ({drug.pakningsinfoPakningsstr || drug.pakningsinfoMengde} {drug.pakningsinfoEnhetPakningV})
                                            <PrescriptionPackageTotal drug={drug} className='small text-primary fw-semibold mt-1' />
                                            <small class='text-muted'>
                                                {drug.preparattypeDn && <>Type: {drug.preparattypeDn}</>}
                                                {drug.atcDn && <>, <span class='font-weight-lighter'>ATC:</span> {drug.atcDn}</>}
                                                {drug.reseptgruppeV && <>, <span class='font-weight-lighter'>Grp:</span> {drug.reseptgruppeV}</>}
                                                {drug.smakDn && <>, <span class='font-weight-lighter'>Smak:</span> {drug.smakDn}</>}
                                                {drug.administrasjonsveiDn && <>, <span class='font-weight-lighter'>Adm:</span> {drug.administrasjonsveiDn}</>}
                                            </small>
                                        </div>
                                    </div>
                                    <div class={`d-flex flex-row justify-content-between px-3 py-1`}>
                                        <div class='text-muted'>
                                            <Text id='dyrejournal.prescription.dose'>Dssn.</Text>
                                        </div>
                                        <div class='flex-fill ms-2'>
                                            {drug.dosage && <>
                                                <Markdown markdown={drug.dosage} markedOpts={localUtil.getMarkdownOptions()} />
                                            </>}
                                            {drug.frequency && <>
                                                {/* {drug.frequency}<br /> */}
                                                Frekvens: <Markdown markdown={drug.frequency} markedOpts={localUtil.getMarkdownOptions()} />
                                            </>}
                                            <small class='text-muted'>
                                                {drug.forhandsregelInntakDn && <>
                                                    {/* Merk: {drug.forhandsregelInntakDn} */}
                                                    Merk:
                                                    <Markdown markdown={drug.forhandsregelInntakDn} markedOpts={localUtil.getMarkdownOptions()} />
                                                </>}
                                            </small>
                                            {drug.description && <>
                                                <small class='text-muted'>
                                                    {/* {drug.description} */}
                                                    <Markdown markdown={drug.description} markedOpts={localUtil.getMarkdownOptions()} />
                                                </small>
                                            </>}
                                            {drug.additionalNotes && <>
                                                <small class='text-muted'>
                                                    {/* {drug.description} */}
                                                    <Markdown markdown={drug.additionalNotes} markedOpts={localUtil.getMarkdownOptions()} />
                                                </small>
                                            </>}
                                        </div>
                                    </div>
                                    {drug.reit && <div class={`d-flex flex-row justify-content-between px-3 py-1`}>
                                        <div class='text-muted'>
                                            <Text id='dyrejournal.prescription.reit'>Reit</Text>
                                        </div>
                                        <div class='flex-fill ms-2'>
                                            {drug.reit}
                                        </div>
                                    </div>}
                                    {/* {drug.preparatomtaleavsnittLenke && <div class={`d-flex flex-row justify-content-between px-3 py-1`}>
                                        <div class='text-muted'>
                                            <Text id='dyrejournal.prescription.about'>About</Text>
                                        </div>
                                        <div class='flex-fill ms-2'>
                                            <a href={drug.preparatomtaleavsnittLenke} native target='_blank'>{drug.preparatomtaleavsnittAvsnittoverskriftDn}</a>
                                        </div>
                                    </div>} */}
                                </div>
                            </>)}
                        </>}

                        {showButtons && <div class='d-flex flex-row flex-wrap justify-content-between px-3 py-1'>
                            <button
                                class={`btn btn-danger ms-2 rounded-pill mt-2`}
                                onClick={(e) => this.onClickPrintPrescription(e, prescription)}
                            >
                                <i class='fa-solid fa-print' /> <Text id='dyrejournal.print-prescription'>Print prescription</Text>
                            </button>
                            <button
                                class={`btn btn-primary ms-2 rounded-pill mt-2`}
                                onClick={(e) => this.onClickEmailPrescription(e, prescription)}
                            >
                                <i class='fa-solid fa-paper-plane' /> <Text id='dyrejournal.email-prescription'>Email prescription</Text>
                            </button>
                            <button
                                class={`btn btn-success ms-2 rounded-pill mt-2`}
                                onClick={(e) => this.onClickCopyPrescription(e, prescription)}
                            >
                                <i class='fa-solid fa-copy' /> <Text id='dyrejournal.copy-prescription'>Copy prescription</Text>
                            </button>
                        </div>}

                    </div>
                </>)}
            </>}

            {!(prescriptions && prescriptions.length > 0) && <>
                <div class={`d-flex flex-column justify-content-center w-100 my-1 text-muted ${classNames}`}>
                    <div class='w-100 text-center' style='font-size: 1.5em;'>
                        <i class='fa-solid fa-prescription' />
                    </div>
                    <div class='w-100 text-center' style='font-size: 1.0em;'>
                        <Text id='dyrejournal.prescription.no-prescription'>No prescription for this journal</Text>
                    </div>
                </div>
            </>}

            {!saleIsLocked && <div class={`d-flex flex-row justify-content-center mt-3 px-3 ${classNames}`}>
                <button class='btn btn-sm btn-outline-primary rounded-pill' onClick={this.onClickCreatePrescription}>
                    <i class='fa-solid fa-prescription me-2' /> <Text id='dyrejournal.prescription.new'>New prescription</Text>
                </button>
                {showDpm && <button class='btn btn-sm btn-outline-primary rounded-pill ms-2' onClick={this.onClickDpmLaunch}>
                    <i class='fa-duotone fa-file-prescription me-2' /> <Text id='dyrejournal.prescription.dpmLaunch'>E-prescription (DyreID)</Text>
                </button>}
            </div>}

        </>);
    }
}

export default PrescriptionListMini;
